// AI risk-narrative card. Renders a short, rotating risk-highlights narrative
// in a compact stack with a regenerate affordance.
//
// The narrative variants are now COMPOSED FROM THE LIVE RISK MODEL
// (ORG_FACTOR_STATS / ORG_SCORE / PERIOD_DATA) via deterministic templates,
// so every number in the copy is real. This is templated composition, not an
// LLM - a server-side generated summary remains the intended end state
// (`useRiskSummaryQuery('exec')`, cache 2h).
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { useTheme, lighten, darken } from '@mui/material/styles';
import MainCard from '../../../../components/cards/MainCard';
import { motion, AnimatePresence } from 'motion/react';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import useFeature from '../../useFeature';
import { ORG_SCORE, ORG_BAND, ORG_DELTA, ORG_FACTOR_STATS, BENCHMARK, formatFactorValue } from '../../riskScore';
import { PERIOD_DATA, SEND_LOG, COURSE_SEND_LOG, USERS } from '../../data';
import useDashboardData from '../../hooks/useDashboardData';
import { hasTenancyScope } from '../../serverScope';

// Compose the narrative variants from the live risk model. Same shape the
// static copy had ({ text: {on, off}, highlights: {on, off}, signals: {on,
// off} }) so the rendering below is untouched. Severity per factor comes
// from its value against the platform anchor (red >10% above, amber within
// 10%, green below).
function factorSeverity(stat) {
    const anchorRef = stat.anchor ? (stat.anchor.industry ?? stat.anchor.platform) : null;
    if (anchorRef == null || anchorRef === 0) return stat.componentMean >= 0.5 ? 'med' : 'low';
    const ratio = stat.componentMean / anchorRef;
    if (ratio > 1.1) return 'high';
    if (ratio > 0.85) return 'med';
    return 'low';
}

// Map factor id -> server risk-aggregate field so the narrative reads the SAME
// server values as the Risk dial and Score-drivers strip (single source of
// truth) rather than the client-recomputed model. Mirrors FactorStrip.
const FACTOR_FIELD = {
    'fail-rate': 'factorFailRate',
    'repeat': 'factorRepeat',
    'stage-depth': 'factorStageDepth',
    'training-debt': 'factorTrainingDebt',
    'course-lag': 'factorCourseLag',
};
const CONTRIB_FIELD = {
    'fail-rate': 'contribFailRate',
    'repeat': 'contribRepeat',
    'stage-depth': 'contribStageDepth',
    'training-debt': 'contribTrainingDebt',
    'course-lag': 'contribCourseLag',
};

// Org band name derived from the server org score (thresholds match the
// dashboard legend: Low 0-29, Medium 30-55, High 56-75, Critical 76+).
function bandForScore(score) {
    if (score >= 76) return 'Critical';
    if (score >= 56) return 'High';
    if (score >= 30) return 'Medium';
    return 'Low';
}

// Builds both the benchmark-on and benchmark-off copy for each variant; the
// component selects which to show from the feature flag at render time. `risk`
// is the server risk aggregate (reportsb_agg.risk_snapshot); when present its
// factor values / org score / scored-user count override the client model so
// the copy matches the Risk dial and Score-drivers exactly. Falls back to the
// client model when absent (e.g. a scoped view with no org aggregate).
function pctOf(n, d, digits) {
    if (!d) return '0';
    const v = (n / d) * 100;
    return digits ? v.toFixed(digits) : Math.round(v).toString();
}

export function buildLiveTotals(performance, riskTrend) {
    const periods = Array.isArray(performance?.periods) ? performance.periods : [];
    const p6 = periods.find((p) => p.period === '6m') || null;
    const pd6 = p6 ? {
        simClickRate: pctOf(p6.clicks, p6.sends, 1),
        reportRate: pctOf(p6.reports, p6.sends, 1),
        courseCompletion: pctOf(p6.courseCompleted, p6.courseSends, 0),
        activeUsers: performance.activeUsers ?? 0,
        sends: p6.sends ?? 0,
        courseSends: p6.courseSends ?? 0,
    } : null;
    let delta = null;
    const pts = Array.isArray(riskTrend) ? riskTrend.filter((p) => p && p.day && typeof p.score === 'number') : [];
    if (pts.length >= 2) {
        const sorted = [...pts].sort((a, b) => a.day.localeCompare(b.day));
        const last = sorted[sorted.length - 1];
        const cutoff = Date.parse(`${last.day}T00:00:00Z`) - 30 * 24 * 60 * 60 * 1000;
        const earlier = sorted.filter((p) => Date.parse(`${p.day}T00:00:00Z`) <= cutoff);
        const base = earlier.length ? earlier[earlier.length - 1] : sorted[0];
        delta = Math.round(last.score - base.score);
    }
    return { pd6, delta };
}

function buildSummaries(risk, live) {
    const factorStats = ORG_FACTOR_STATS.map((s) => {
        const f = FACTOR_FIELD[s.id];
        const c = CONTRIB_FIELD[s.id];
        const next = (risk && f && risk[f] != null) ? { ...s, componentMean: risk[f] } : s;
        return (live && risk && typeof risk[c] === 'number') ? { ...next, avgContribution: risk[c] } : next;
    });
    const score = (risk && typeof risk.orgScore === 'number') ? risk.orgScore : (ORG_SCORE ?? 0);
    const bandName = (risk && typeof risk.orgScore === 'number') ? bandForScore(score) : (ORG_BAND?.name || 'Unscored');
    const scoredUsers = (risk && typeof risk.scoredUsers === 'number') ? risk.scoredUsers : null;
    const stats = [...factorStats].sort((a, b) => b.avgContribution - a.avgContribution);
    const top = stats[0];
    const second = stats[1];
    const pd6 = live ? (live.pd6 || {}) : (PERIOD_DATA['6m'] || {});
    const orgDelta = live ? live.delta : ORG_DELTA;
    const deltaWord = orgDelta > 0 ? 'risen' : orgDelta < 0 ? 'fallen' : 'held steady';

    const variants = [];

    if (top) {
        const topVal = formatFactorValue(top, top.componentMean);
        const sampleN = scoredUsers != null ? scoredUsers : top.sampleSize;
        // Course factors average only users with courses assigned, so the
        // scored-user count would misstate the cohort - describe it instead.
        const isCourseFactor = top.id === 'training-debt' || top.id === 'course-lag';
        const cohort = isCourseFactor ? 'users with training assigned' : `the ${sampleN} scored users`;
        const secondPart = second
            ? ` ${second.label} is the next largest contributor at ${second.weight}% of the weighting, currently averaging ${formatFactorValue(second, second.componentMean)}.`
            : '';
        const text = `${top.label} is the heaviest driver of your risk score at ${top.weight}% of the weighting, averaging ${topVal} across ${cohort} in the rolling 6-month window.${secondPart}`;
        variants.push({
            text: { on: text, off: text },
            highlights: {
                on:  [top.label, `${top.weight}% of the weighting`, ...(second ? [second.label] : [])],
                off: [top.label, `${top.weight}% of the weighting`, ...(second ? [second.label] : [])],
            },
            signals: {
                on:  stats.slice(0, 4).map((s) => ({ text: `${s.label} ${formatFactorValue(s, s.componentMean)}`, severity: factorSeverity(s) })),
                off: stats.slice(0, 4).map((s) => ({ text: `${s.label} ${formatFactorValue(s, s.componentMean)}`, severity: factorSeverity(s) })),
            },
        });
    }

    {
        const clickRate = pd6.simClickRate ?? '0';
        const reportRate = pd6.reportRate ?? '0';
        const completion = pd6.courseCompletion ?? '0';
        const usersLine = live
            ? `${pd6.activeUsers ?? 0} users have a risk score in the period.`
            : `${pd6.engagedUsers ?? 0} users were active in the period.`;
        const text = `Over the last 6 months the click rate across delivered simulations is ${clickRate}% with ${reportRate}% reported, and ${completion}% of assigned courses were completed. ${usersLine}`;
        const sigs = [
            { text: `Click rate ${clickRate}%`, severity: parseFloat(clickRate) >= 30 ? 'high' : parseFloat(clickRate) >= 15 ? 'med' : 'low' },
            { text: `Report rate ${reportRate}%`, severity: parseFloat(reportRate) >= 10 ? 'low' : 'med' },
            { text: `Completion ${completion}%`, severity: parseInt(completion, 10) >= 75 ? 'low' : parseInt(completion, 10) >= 50 ? 'med' : 'high' },
            { text: live ? `${pd6.activeUsers ?? 0} scored users` : `${pd6.engagedUsers ?? 0} engaged users`, severity: 'low' },
        ];
        variants.push({
            text: { on: text, off: text },
            highlights: {
                on:  [`${clickRate}%`, `${reportRate}% reported`, `${completion}% of assigned courses`],
                off: [`${clickRate}%`, `${reportRate}% reported`, `${completion}% of assigned courses`],
            },
            signals: { on: sigs, off: sigs },
        });
    }

    {
        const platformPart = BENCHMARK.platformAvg != null
            ? ` The platform average across ${BENCHMARK.cohortSize} active orgs sits at ${BENCHMARK.platformAvg}.`
            : '';
        const hasDelta = orgDelta != null;
        const deltaPart = hasDelta
            ? ` and has ${deltaWord}${orgDelta !== 0 ? ` by ${Math.abs(orgDelta)} points` : ''} over the last 30 days`
            : '';
        const onText = `Your org risk score is ${score} (${bandName})${deltaPart}.${platformPart}`;
        const offText = `Your org risk score is ${score} (${bandName})${deltaPart}.`;
        const baseSigs = [
            { text: `Score ${score} · ${bandName}`, severity: score >= 56 ? 'high' : score >= 30 ? 'med' : 'low' },
            ...(hasDelta ? [{ text: orgDelta > 0 ? `Up ${orgDelta} pts / 30d` : orgDelta < 0 ? `Down ${Math.abs(orgDelta)} pts / 30d` : 'Flat over 30d', severity: orgDelta > 0 ? 'high' : orgDelta < 0 ? 'low' : 'med' }] : []),
        ];
        const deltaHighlights = hasDelta ? [deltaWord] : [];
        variants.push({
            text: { on: onText, off: offText },
            highlights: {
                on:  [`${score} (${bandName})`, ...deltaHighlights, ...(BENCHMARK.platformAvg != null ? [`platform average`] : [])],
                off: [`${score} (${bandName})`, ...deltaHighlights],
            },
            signals: {
                on:  BENCHMARK.platformAvg != null
                    ? [...baseSigs, { text: `Platform avg ${BENCHMARK.platformAvg}`, severity: score > BENCHMARK.platformAvg ? 'high' : 'low' }]
                    : baseSigs,
                off: baseSigs,
            },
        });
    }

    return variants.length > 0 ? variants : [{
        text: { on: 'No scored activity in the current window yet.', off: 'No scored activity in the current window yet.' },
        highlights: { on: [], off: [] },
        signals: { on: [], off: [] },
    }];
}

function highlightText(text, highlights) {
    if (!highlights.length) return [{ type: 'text', value: text, key: 0 }];
    const pattern = highlights.map((h) => h.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
    const regex = new RegExp(`(${pattern})`, 'gi');
    return text.split(regex).map((part, i) => ({
        type: highlights.some((h) => h.toLowerCase() === part.toLowerCase()) ? 'highlight' : 'text',
        value: part,
        key: i,
    }));
}

function TypewriterText({ text, highlights, speed = 18 }) {
    const [displayed, setDisplayed] = useState('');
    const ref = useRef(null);

    useEffect(() => {
        setDisplayed('');
        let i = 0;
        ref.current = setInterval(() => {
            i++;
            setDisplayed(text.slice(0, i));
            if (i >= text.length) clearInterval(ref.current);
        }, speed);
        return () => clearInterval(ref.current);
    }, [text, speed]);

    const parts = highlightText(displayed, highlights);

    return (
        <Typography sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.8, minHeight: 80 }}>
            {parts.map((p) =>
                p.type === 'highlight' ? (
                    <Box key={p.key} component="span" sx={{ fontWeight: 650, color: 'text.primary' }}>{p.value}</Box>
                ) : (
                    <span key={p.key}>{p.value}</span>
                )
            )}
            {displayed.length < text.length && (
                <Box component="span" sx={{
                    display: 'inline-block', width: '2px', height: '0.9em',
                    bgcolor: 'text.secondary', ml: '1px', verticalAlign: 'middle',
                    '@keyframes blink': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0 } },
                    animation: 'blink 0.7s step-end infinite',
                }} />
            )}
        </Typography>
    );
}

const severityColor = { high: '#FF5252', med: '#FFCA28', low: '#66BB6A' };

function hexLuminance(hex) {
    const c = hex.replace('#', '');
    const r = parseInt(c.slice(0, 2), 16) / 255;
    const g = parseInt(c.slice(2, 4), 16) / 255;
    const b = parseInt(c.slice(4, 6), 16) / 255;
    const toLinear = (v) => v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
}

function gradientTextColor(primary, secondary) {
    const avgLum = (hexLuminance(primary) + hexLuminance(secondary)) / 2;
    return avgLum > 0.35 ? '#111418' : '#ffffff';
}

export default function AICard({ go, risk, scope }) {
    const theme = useTheme();
    const tenancyScoped = hasTenancyScope(scope);
    const summaryQ = useDashboardData({ metric: 'simulations', aggregation: 'summary', scope }, { skip: !tenancyScoped });
    const trendQ = useDashboardData({ metric: 'simulations', aggregation: 'risk_trend', scope }, { skip: !tenancyScoped });
    const live = useMemo(
        () => (tenancyScoped ? buildLiveTotals(summaryQ.data?.performance, trendQ.data?.riskTrend) : null),
        [tenancyScoped, summaryQ.data, trendQ.data],
    );
    const benchmarkOn = useFeature('industryBenchmarking');
    const primary = theme.palette.primary.main;
    const secondary = theme.palette.secondary.main;

    const lum = hexLuminance(primary);
    const isBlack = lum < 0.02;
    const isWhite = lum > 0.95;
    const btnTextColor = lum > 0.35 ? '#111418' : '#ffffff';

    const light = lighten(primary, 0.35);
    const dark = darken(primary, 0.08);
    const gradient = (isBlack || isWhite)
        ? primary
        : `linear-gradient(315deg, ${dark}, ${light}, ${dark})`;
    const gradientStatic = (isBlack || isWhite)
        ? primary
        : `linear-gradient(315deg, ${dark}, ${light})`;

    const SUMMARIES = useMemo(() => buildSummaries(risk, live), [risk, live]);

    const [idx, setIdx] = useState(0);
    const [regenerating, setRegenerating] = useState(false);
    const [ts, setTs] = useState(() => new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }));
    const [typing, setTyping] = useState(false);

    function handleRegenerate() {
        setRegenerating(true);
        setTyping(false);
        setTimeout(() => {
            setIdx((i) => (i + 1) % SUMMARIES.length);
            setTs(new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }));
            setRegenerating(false);
            setTyping(true);
        }, 1100);
    }

    const variant = SUMMARIES[idx % SUMMARIES.length];
    const key = benchmarkOn ? 'on' : 'off';
    const summary = { text: variant.text[key], highlights: variant.highlights[key], signals: variant.signals[key] };

    return (
        <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, ease: 'easeOut' }}
            style={{ height: '100%' }}
        >
            <MainCard content={false} sx={{ bgcolor: 'background.paper', height: '100%', overflow: 'hidden' }} border={false} boxShadow={true}>
                <Box sx={{ p: 3, pb: 2, display: 'flex', flexDirection: 'column', height: '100%', boxSizing: 'border-box' }}>
                    {/* Header */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexShrink: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{
                                width: 20, height: 20, borderRadius: '5px',
                                background: gradientStatic,
                                boxShadow: `0 2px 8px ${dark}40`,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                color: 'common.white',
                                flexShrink: 0,
                            }}>
                                <AutoAwesomeIcon sx={{ fontSize: 13 }} />
                            </Box>
                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.primary', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                Risk Summary
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {regenerating && (
                                <Box sx={{ display: 'flex', gap: '3px', alignItems: 'center' }}>
                                    {[0, 1, 2].map((i) => (
                                        <Box key={i} sx={{
                                            width: 4, height: 4, borderRadius: '50%',
                                            bgcolor: 'text.disabled',
                                            '@keyframes bounce': {
                                                '0%,80%,100%': { transform: 'scale(0.6)', opacity: 0.4 },
                                                '40%': { transform: 'scale(1)', opacity: 1 },
                                            },
                                            animation: `bounce 1s ease-in-out ${i * 0.16}s infinite`,
                                        }} />
                                    ))}
                                </Box>
                            )}
                            <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>
                                {regenerating ? 'Generating...' : `Updated ${ts}`}
                            </Typography>
                        </Box>
                    </Box>

                    {/* Body, fixed height so typewriter text doesn't resize the card */}
                    <Box sx={{ minHeight: 190, flexShrink: 0, position: 'relative' }}>
                        <AnimatePresence mode="wait">
                            {regenerating ? (
                                <motion.div
                                    key="regenerating"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    transition={{ duration: 0.15 }}
                                >
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                                        {[92, 100, 78, 55].map((w, i) => (
                                            <Box key={i} sx={{
                                                height: 9, borderRadius: 1, bgcolor: 'divider', width: `${w}%`,
                                                '@keyframes shimmer': {
                                                    '0%': { opacity: 0.3 }, '50%': { opacity: 0.7 }, '100%': { opacity: 0.3 },
                                                },
                                                animation: `shimmer 1.4s ease-in-out ${i * 0.18}s infinite`,
                                            }} />
                                        ))}
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                                        {[80, 100, 70, 90].map((w, i) => (
                                            <Box key={i} sx={{
                                                height: 24, borderRadius: '6px', bgcolor: 'divider', width: w,
                                                '@keyframes shimmer': {
                                                    '0%': { opacity: 0.3 }, '50%': { opacity: 0.7 }, '100%': { opacity: 0.3 },
                                                },
                                                animation: `shimmer 1.4s ease-in-out ${i * 0.12}s infinite`,
                                            }} />
                                        ))}
                                    </Box>
                                </motion.div>
                            ) : (
                                <motion.div
                                    key={idx}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    transition={{ duration: 0.2 }}
                                >
                                    <Box sx={{ mb: 1.5 }}>
                                        {typing ? (
                                            <TypewriterText text={summary.text} highlights={summary.highlights} />
                                        ) : (
                                            <Typography sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.8 }}>
                                                {highlightText(summary.text, summary.highlights).map((p) =>
                                                    p.type === 'highlight' ? (
                                                        <Box key={p.key} component="span" sx={{ fontWeight: 650, color: 'text.primary' }}>{p.value}</Box>
                                                    ) : (
                                                        <span key={p.key}>{p.value}</span>
                                                    )
                                                )}
                                            </Typography>
                                        )}
                                    </Box>

                                    {/* Signal chips */}
                                    <Box sx={{ display: 'flex', gap: 0.6, flexWrap: 'wrap' }}>
                                        {summary.signals.map((s, i) => (
                                            <motion.div
                                                key={i}
                                                initial={{ opacity: 0, y: 4 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: typing ? 0.8 + i * 0.08 : 0, duration: 0.2 }}
                                            >
                                                <Box sx={{
                                                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                                                    fontSize: 11, fontWeight: 600,
                                                    px: '10px', py: '4px',
                                                    borderRadius: '6px',
                                                    border: '1px solid',
                                                    borderColor: `${severityColor[s.severity]}50`,
                                                    color: severityColor[s.severity],
                                                    bgcolor: `${severityColor[s.severity]}12`,
                                                    letterSpacing: '0.01em',
                                                }}>
                                                    <Box sx={{
                                                        width: 6, height: 6, borderRadius: '2px',
                                                        bgcolor: severityColor[s.severity],
                                                        flexShrink: 0,
                                                    }} />
                                                    {s.text}
                                                </Box>
                                            </motion.div>
                                        ))}
                                    </Box>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </Box>

                    {/* Footer */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 1.5, mt: 1, flexShrink: 0 }}>
                        <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>
                            {live
                                ? `${(live.pd6?.sends ?? 0).toLocaleString()} sim sends - ${(live.pd6?.courseSends ?? 0).toLocaleString()} assignments - ${(live.pd6?.activeUsers ?? 0).toLocaleString()} scored users (6 months)`
                                : `${SEND_LOG.length.toLocaleString()} sim events - ${COURSE_SEND_LOG.length.toLocaleString()} assignments - ${USERS.length.toLocaleString()} users`}
                        </Typography>
                    </Box>
                </Box>
            </MainCard>
        </motion.div>
    );
}
